// Templates de tâches légales spécifiques par pays

export interface LegalTaskTemplate {
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high';
  daysBeforeEvent: number;
  countries: string[]; // Codes pays (FR, BE, CH, etc.)
  eventTypes: string[]; // Types d'événements concernés
  isRequired: boolean; // Obligatoire ou recommandé
}

// Tâches légales pour la France
export const franceLegalTasks: LegalTaskTemplate[] = [
  // Mariage
  {
    title: 'Publier les bans de mariage',
    description: 'Affichage obligatoire des bans à la mairie 10 jours avant le mariage',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 15,
    countries: ['FR'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Constituer le dossier de mariage',
    description: 'Rassembler tous les documents requis : actes de naissance, justificatifs de domicile, etc.',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['FR'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Choisir le régime matrimonial',
    description: 'Décider du régime matrimonial et éventuellement prendre rendez-vous chez le notaire',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['FR'],
    eventTypes: ['wedding'],
    isRequired: false
  },
  {
    title: 'Obtenir un certificat médical prénuptial',
    description: 'Bien que non obligatoire depuis 2007, certaines mairies le demandent encore',
    category: 'legal',
    priority: 'low',
    daysBeforeEvent: 30,
    countries: ['FR'],
    eventTypes: ['wedding'],
    isRequired: false
  },
  
  // Baptême
  {
    title: 'Demander l\'acte de naissance pour le baptême',
    description: 'Obtenir l\'acte de naissance de l\'enfant auprès de la mairie',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 21,
    countries: ['FR'],
    eventTypes: ['baptism'],
    isRequired: true
  },
  {
    title: 'Vérifier l\'âge des parrains/marraines',
    description: 'S\'assurer que les parrains/marraines ont plus de 16 ans (requis par l\'Église)',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 30,
    countries: ['FR'],
    eventTypes: ['baptism'],
    isRequired: true
  }
];

// Tâches légales pour la Belgique
export const belgiumLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Déclarer l\'intention de mariage',
    description: 'Déposer la déclaration d\'intention de mariage au moins 14 jours avant la cérémonie',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 21,
    countries: ['BE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Fournir les documents d\'état civil',
    description: 'Actes de naissance, certificats de célibat, etc. selon la commune',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['BE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Choisir le régime matrimonial belge',
    description: 'Opter pour la communauté légale ou un autre régime devant notaire',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['BE'],
    eventTypes: ['wedding'],
    isRequired: false
  }
];

// Tâches légales pour la Suisse
export const switzerlandLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Effectuer la préparation au mariage',
    description: 'Procédure préparatoire obligatoire auprès de l\'officier d\'état civil',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 90,
    countries: ['CH'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Publier le mariage',
    description: 'Publication officielle du mariage pendant 10 jours',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 20,
    countries: ['CH'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Présenter les documents d\'identité',
    description: 'Passeports ou cartes d\'identité valides des futurs époux',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['CH'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour le Canada
export const canadaLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Obtenir une licence de mariage',
    description: 'Demander la licence de mariage auprès du bureau d\'état civil provincial',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['CA'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Vérifier les exigences de résidence',
    description: 'S\'assurer de respecter les exigences de résidence de la province',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['CA'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Prévoir les témoins légaux',
    description: 'Avoir au moins 2 témoins majeurs présents lors de la cérémonie',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 14,
    countries: ['CA'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour les États-Unis
export const usaLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Obtenir une marriage license',
    description: 'Demander la licence de mariage auprès du County Clerk Office',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['US'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Respecter la période d\'attente',
    description: 'Certains États imposent une période d\'attente entre la demande et la cérémonie',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 7,
    countries: ['US'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Vérifier les exigences de l\'État',
    description: 'Chaque État a ses propres exigences (âge, tests sanguins, etc.)',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['US'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour l'Espagne
export const spainLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Solicitar expediente matrimonial',
    description: 'Presentar solicitud de expediente matrimonial en el Registro Civil',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['ES'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Publicar las amonestaciones',
    description: 'Publicación de amonestaciones durante 15 días hábiles',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 20,
    countries: ['ES'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Presentar documentos de estado civil',
    description: 'Certificados de nacimiento, soltería y empadronamiento',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['ES'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour l'Italie
export const italyLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Pubblicare i bandi matrimoniali',
    description: 'Pubblicazione dei bandi per 8 giorni consecutivi',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 15,
    countries: ['IT'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Presentare documenti di stato civile',
    description: 'Certificati di nascita, stato libero e residenza',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['IT'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Scegliere il regime patrimoniale',
    description: 'Decidere tra comunione o separazione dei beni',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['IT'],
    eventTypes: ['wedding'],
    isRequired: false
  }
];

// Tâches légales pour l'Allemagne
export const germanyLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Ehefähigkeitszeugnis beantragen',
    description: 'Antrag auf Ehefähigkeitszeugnis beim Standesamt',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['DE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Eheaufgebot veröffentlichen',
    description: 'Veröffentlichung des Eheaufgebots für 10 Tage',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 15,
    countries: ['DE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Güterstand wählen',
    description: 'Entscheidung über Zugewinngemeinschaft oder Gütertrennung',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['DE'],
    eventTypes: ['wedding'],
    isRequired: false
  }
];

// Tâches légales pour le Royaume-Uni
export const ukLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Give notice of marriage',
    description: 'Give notice at least 28 days before the ceremony',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 35,
    countries: ['GB', 'UK'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Obtain marriage license',
    description: 'Apply for marriage license at local registry office',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['GB', 'UK'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Arrange civil ceremony',
    description: 'Book civil ceremony at registry office or approved venue',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 60,
    countries: ['GB', 'UK'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour le Pays de Galles
export const walesLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Give notice of marriage',
    description: 'Give notice of marriage at local register office (28 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 28,
    countries: ['WLS'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Obtain marriage license',
    description: 'Obtain marriage license from local authority (30 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['WLS'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Arrange civil ceremony',
    description: 'Book civil ceremony venue and registrar (60 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 60,
    countries: ['WLS'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour l'Écosse
export const scotlandLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Submit marriage notice',
    description: 'Submit marriage notice to registrar (28 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 28,
    countries: ['SCT'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Obtain marriage schedule',
    description: 'Obtain marriage schedule from registrar (30 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['SCT'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Book ceremony venue',
    description: 'Book ceremony venue and celebrant (60 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 60,
    countries: ['SCT'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour l'Irlande du Nord
export const northernIrelandLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Give notice of marriage',
    description: 'Give notice of marriage at local register office (28 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 28,
    countries: ['NIR'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Obtain marriage license',
    description: 'Obtain marriage license from local authority (30 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['NIR'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Arrange civil ceremony',
    description: 'Book civil ceremony venue and registrar (60 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 60,
    countries: ['NIR'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales pour l'Irlande
export const irelandLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Give notice of marriage',
    description: 'Give notice of marriage at civil registration office (3 months before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 90,
    countries: ['IE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Obtain marriage license',
    description: 'Obtain marriage license from civil registration office (30 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['IE'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Book ceremony venue',
    description: 'Book ceremony venue and celebrant (60 days before)',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 60,
    countries: ['IE'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Tâches légales génériques pour les pays non spécifiés
export const genericLegalTasks: LegalTaskTemplate[] = [
  {
    title: 'Vérifier les exigences légales locales',
    description: 'Se renseigner auprès des autorités locales sur les démarches requises',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 60,
    countries: ['*'], // Tous pays
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Rassembler les documents d\'identité',
    description: 'Préparer tous les documents d\'identité et d\'état civil nécessaires',
    category: 'legal',
    priority: 'high',
    daysBeforeEvent: 30,
    countries: ['*'],
    eventTypes: ['wedding'],
    isRequired: true
  },
  {
    title: 'Prévoir les témoins requis',
    description: 'S\'assurer d\'avoir le nombre de témoins requis par la législation locale',
    category: 'legal',
    priority: 'medium',
    daysBeforeEvent: 14,
    countries: ['*'],
    eventTypes: ['wedding'],
    isRequired: true
  }
];

// Mapping des codes pays vers leurs tâches légales
export const legalTasksByCountry = {
  'FR': franceLegalTasks,
  'BE': belgiumLegalTasks,
  'CH': switzerlandLegalTasks,
  'ES': spainLegalTasks,
  'IT': italyLegalTasks,
  'DE': germanyLegalTasks,
  'GB': ukLegalTasks,
  'WLS': walesLegalTasks,
  'SCT': scotlandLegalTasks,
  'NIR': northernIrelandLegalTasks,
  'IE': irelandLegalTasks,
  'CA': canadaLegalTasks,
  'US': usaLegalTasks,
  '*': genericLegalTasks // Fallback pour les autres pays
};

// Fonction pour obtenir les tâches légales selon le pays et le type d'événement
export function getLegalTasksForCountryAndEvent(country: string, eventType: string): LegalTaskTemplate[] {
  const countryTasks = legalTasksByCountry[country as keyof typeof legalTasksByCountry] || legalTasksByCountry['*'];
  
  return countryTasks.filter(task => 
    task.eventTypes.includes(eventType) || task.eventTypes.includes('*')
  );
}

// Fonction pour obtenir toutes les tâches légales disponibles
export function getAllLegalTasks(): LegalTaskTemplate[] {
  return [
    ...franceLegalTasks,
    ...belgiumLegalTasks,
    ...switzerlandLegalTasks,
    ...spainLegalTasks,
    ...italyLegalTasks,
    ...germanyLegalTasks,
    ...ukLegalTasks,
    ...walesLegalTasks,
    ...scotlandLegalTasks,
    ...northernIrelandLegalTasks,
    ...irelandLegalTasks,
    ...canadaLegalTasks,
    ...usaLegalTasks,
    ...genericLegalTasks
  ];
} 